import { Award, Clock, Users, ShieldCheck, Paintbrush, TrendingUp } from "lucide-react";
import { useScrollReveal } from "../hooks/use-scroll-reveal";

const reasons = [
  {
    icon: Clock,
    value: "10+",
    label: "Years Experience",
    description: "A decade of painting excellence in Central Florida",
  },
  {
    icon: Paintbrush,
    value: "1000+",
    label: "Projects Completed",
    description: "Interior, exterior, and specialty coating projects",
  },
  {
    icon: ShieldCheck,
    value: "100%",
    label: "Fully Insured",
    description: "We've got you covered",
  },
  {
    icon: Users,
    value: "15+",
    label: "Expert Painters",
    description: "Skilled professionals dedicated to quality",
  },
  {
    icon: Award,
    value: "5-Star",
    label: "Rated Service",
    description: "Top-rated on Google, Yelp & Angi",
  },
  {
    icon: TrendingUp,
    value: "98%",
    label: "Client Satisfaction",
    description: "Consistently exceeding expectations",
  },
];

export const WhyChooseUs = () => {
  const [gridRef, gridVisible] = useScrollReveal();

  return (
    <section
      data-testid="why-choose-us-section"
      className="py-20 sm:py-28 bg-white relative overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-1/3 h-full bg-brand-ice -skew-x-12 transform origin-top-right" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center mb-12 sm:mb-14">
          <div>
            <span className="inline-block text-xs sm:text-sm font-medium uppercase tracking-widest text-brand-orange mb-3">
              Why Choose Us
            </span>
            <h2
              data-testid="why-choose-heading"
              className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight uppercase text-brand-navy"
            >
              Building Trust Through
              <span className="text-brand-orange"> Excellence</span>
            </h2>
          </div>
          <div>
            <p className="font-body text-sm sm:text-base text-brand-slate leading-relaxed">
              10+ years of experience protecting and beautifying Central Florida homes with paint engineered for the Florida climate.
            </p>
          </div>
        </div>

        <div
          ref={gridRef}
          className={`grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 reveal-stagger ${gridVisible ? 'visible' : ''}`}
        >
          {reasons.map((reason, index) => {
            const Icon = reason.icon;
            return (
              <div
                key={reason.label}
                data-testid={`why-card-${index}`}
                className="group relative bg-white border border-border/60 rounded-xl p-6 hover:border-brand-orange/40 card-lift"
              >
                {/* Number */}
                <div className="absolute top-3 right-4 font-heading text-5xl font-semibold text-brand-ice group-hover:text-brand-orange/10 transition-colors duration-300">
                  {String(index + 1).padStart(2, "0")}
                </div>

                <div className="relative z-10">
                  <div className="w-12 h-12 bg-brand-orange rounded-lg flex items-center justify-center mb-5 group-hover:bg-brand-orange/90 transition-colors duration-300">
                    <Icon className="w-6 h-6 text-white transition-colors duration-300" />
                  </div>

                  <div className="font-heading text-4xl font-bold text-brand-navy mb-1">
                    {reason.value}
                  </div>
                  <h3 className="font-heading text-base sm:text-lg font-semibold uppercase text-brand-navy mb-1.5">
                    {reason.label}
                  </h3>
                  <p className="text-brand-slate text-sm">
                    {reason.description}
                  </p>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-b-xl" />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
